// Code browser hooks: CodeEditor, PaneDivider, ReadmeHighlight,
// VimToggle, EditorModeButton, KeyboardShortcuts.
// Dense coupling via shared sidebar persistence and CM module.

import gsap from "gsap";
import { Socket } from "phoenix";
import { readUserSocketToken } from "../user-socket-token";
import * as CM from "../codemirror";
import { Vim } from "@replit/codemirror-vim";
import type { EditorView } from "codemirror";
import { playLineTransition } from "../history-viewer";
import type {
  BlameBlock,
  SetCodePayload,
  SetCommentsPayload,
  WorkspaceFileChangedPayload,
} from "../../../.dox/ui";
import { connectLSP } from "../lsp-client";
import {
  attachLspConnection,
  detachLspConnection,
  type AttachDeps,
  type LspState,
  type SocketFactory,
} from "../lsp-hook-connection";
import {
  persistSidebarWidth,
  clearGsapResiduals,
  isEditableTarget,
} from "./types";
import type { LiveViewHook, EditorPushEventWire } from "./types";
import {
  illuminateCssClassCodeBrowser,
  illuminateDomEvent,
  validDomEvent,
  validEditorElementId,
} from "../../../.dox/ui";
import { codeSidebarDefaultWidth, codeSidebarMaxFraction } from "../../../.dox";
import * as Storage from "../local-storage";
import type { LocalStorageKey } from "../local-storage";

type EditorHookLike = LiveViewHook<EditorPushEventWire>;

// Element ids are dox-owned (`EditorElementId` in `ui/editor.dox`); a
// rename upstream surfaces here as a tsc error instead of a silent
// `null` from `getElementById`. We need the JSON wire form (what
// Elixir's `to_json/1` renders into the DOM), not the human-readable
// illumination — illuminate* is for display strings, not ids.
const CODE_VIEWER_ID = validEditorElementId("codeViewer");
// The unsaved-changes dot in the repo header. Routed through the
// generated EditorElementId registry (same contract as CODE_VIEWER_ID)
// so a rename of the union member fails at startup instead of the
// lookup silently returning null forever (salvage C14d).
const SHORTCUTS_OVERLAY_ID = validEditorElementId("shortcutsOverlay");
const RICH_EDITOR_ID = validEditorElementId("richEditor");

// CSS class names shared between this hook and the HEEx code-browser
// page (`lib/operation_web/live/repo_show_live.ex`'s `<style>` block
// + `<code-editor>` root). Pinned through `CssClassCodeBrowser`.
const BLAME_ACTIVE = illuminateCssClassCodeBrowser("blameActive");
const BLAME_DIVIDER = illuminateCssClassCodeBrowser("blameDivider");
const CM_BLAME_GUTTER = illuminateCssClassCodeBrowser("cmBlameGutter");
const DRAGGING = illuminateCssClassCodeBrowser("dragging");
const RESIZING = illuminateCssClassCodeBrowser("resizing");
const SIDEBAR_HIDDEN = illuminateCssClassCodeBrowser("sidebarHidden");
const SIDEBAR_CLOSE_BTN = illuminateCssClassCodeBrowser("sidebarCloseBtn");

// --- Blame divider: drag-resize the blame gutter width ---

// WHY local: blame-gutter sizing has no server-rendered HEEx counterpart.
// The blame divider element is created entirely client-side (see
// `positionBlameDivider`) and the upper bound is purely a usability
// clamp for the drag handler. No Elixir or HEEx code reads these values,
// so they stay as TS-local constants.
const BLAME_WIDTH_MAX = 600;
const BLAME_DISMISS_THRESHOLD = 20;

// Pure: clamp a drag x-offset to the allowed blame gutter width range
export const clampBlameWidth = (clientX: number, gutterLeft: number): number =>
  Math.max(0, Math.min(BLAME_WIDTH_MAX, clientX - gutterLeft));

// Pure: decide whether to persist or dismiss blame after a resize
export type BlameResizeResult =
  | { readonly action: "persist"; readonly width: number }
  | { readonly action: "dismiss" };

export const blameResizeResult = (width: number): BlameResizeResult =>
  width < BLAME_DISMISS_THRESHOLD
    ? { action: "dismiss" }
    : { action: "persist", width: Math.round(width) };

// DOM: position the divider element at the right edge of the blame gutter
export const positionBlameDivider = (
  divider: HTMLElement,
  gutterLeft: number,
  gutterWidth: number,
): void => {
  divider.style.left = `${gutterLeft + gutterWidth}px`;
};

// DOM: toggle the drag-active cursor on body
const setDragCursor = (active: boolean): void => {
  document.body.style.userSelect = active ? "none" : "";
  document.body.style.cursor = active ? "col-resize" : "";
};

// Build the three drag event handlers with encapsulated dragging state.
// Pure side-effect boundary: drag mechanics call onResize during drag
// and onComplete at the end, letting the caller decide what to do.
export const makeBlameDragHandlers = (
  divider: HTMLElement,
  blameGutter: HTMLElement,
  onResize: (width: number) => void,
  onComplete: (result: BlameResizeResult) => void,
): {
  readonly onDown: (e: MouseEvent) => void;
  readonly onMove: (e: MouseEvent) => void;
  readonly onUp: () => void;
} => {
  let dragging = false;

  return {
    onDown: (e: MouseEvent) => {
      e.preventDefault();
      dragging = true;
      divider.classList.add(DRAGGING);
      setDragCursor(true);
    },
    onMove: (e: MouseEvent) => {
      if (!dragging) return;
      const rect = blameGutter.getBoundingClientRect();
      onResize(clampBlameWidth(e.clientX, rect.left));
    },
    onUp: () => {
      if (!dragging) return;
      dragging = false;
      divider.classList.remove(DRAGGING);
      setDragCursor(false);
      onComplete(blameResizeResult(blameGutter.getBoundingClientRect().width));
    },
  };
};

// ---------------------------------------------------------------------------
// BlameDividerController
// ---------------------------------------------------------------------------
//
// Owns one blame-divider lifecycle: creation, drag wiring, persistence, and
// teardown. The constructor takes the dependencies it needs to be testable
// without a full DOM/localStorage stack:
//
//   - `doc`        — the `Document` to mutate (window event listeners + body
//                    cursor are routed through the document's `defaultView`).
//   - `storage`    — typed storage adapter (defaults to the module-level
//                    `Storage` wrapper around `localStorage`).
//   - `notifyDismiss` — fired when the drag ends with a "dismiss" verdict;
//                    in production this is `hook.pushEvent("toggleBlame", {})`.
//
// Pure core: the controller never reads from the DOM directly outside of
// the viewer element it was constructed with — every other side effect is
// routed through the injected handles.

export interface BlameStorage {
  setItem(key: LocalStorageKey, value: string): void;
  getItem(key: LocalStorageKey): string | null;
  removeItem(key: LocalStorageKey): void;
}

export interface BlameDividerDeps {
  readonly doc: Document;
  readonly storage: BlameStorage;
  readonly notifyDismiss: () => void;
}

export class BlameDividerController {
  private cleanup: (() => void) | null = null;

  constructor(
    private readonly viewer: HTMLElement,
    private readonly deps: BlameDividerDeps,
  ) {}

  attach(): void {
    if (this.cleanup) return;
    if (this.viewer.querySelector(`.${BLAME_DIVIDER}`)) return;

    const gutters = this.viewer.querySelector<HTMLElement>(".cm-gutters");
    if (!gutters) return;
    const blameGutter = this.viewer.querySelector<HTMLElement>(
      `.${CM_BLAME_GUTTER}`,
    );
    if (!blameGutter) return;

    const divider = this.deps.doc.createElement("div");
    divider.className = BLAME_DIVIDER;
    gutters.style.position = "relative";
    gutters.appendChild(divider);
    const view = this.deps.doc.defaultView ?? window;
    view.requestAnimationFrame(() => {
      positionBlameDivider(
        divider,
        blameGutter.offsetLeft,
        blameGutter.offsetWidth,
      );
    });

    const { onDown, onMove, onUp } = makeBlameDragHandlers(
      divider,
      blameGutter,
      (width) => {
        this.viewer.style.setProperty("--blame-w", `${width}px`);
        positionBlameDivider(
          divider,
          blameGutter.offsetLeft,
          blameGutter.offsetWidth,
        );
      },
      (result) => {
        if (result.action === "dismiss") {
          this.deps.storage.removeItem("blameWidth");
          this.deps.notifyDismiss();
        } else {
          this.deps.storage.setItem("blameWidth", String(result.width));
        }
      },
    );

    divider.addEventListener("mousedown", onDown);
    view.addEventListener("mousemove", onMove);
    view.addEventListener("mouseup", onUp);

    this.cleanup = () => {
      divider.remove();
      view.removeEventListener("mousemove", onMove);
      view.removeEventListener("mouseup", onUp);
    };
  }

  detach(): void {
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }

  isAttached(): boolean {
    return this.cleanup !== null;
  }
}

// WeakMap to track controllers per viewer element without polluting the DOM node
const blameControllers = new WeakMap<HTMLElement, BlameDividerController>();

function setupBlameDivider(viewer: HTMLElement, hook: EditorHookLike): void {
  const existing = blameControllers.get(viewer);
  if (existing && existing.isAttached()) return;
  const controller =
    existing ??
    new BlameDividerController(viewer, {
      doc: viewer.ownerDocument,
      storage: Storage,
      notifyDismiss: () => {
        hook.pushEvent("toggleBlame", {});
      },
    });
  blameControllers.set(viewer, controller);
  controller.attach();
}

function removeBlameDivider(viewer: HTMLElement): void {
  const controller = blameControllers.get(viewer);
  if (controller) {
    controller.detach();
    blameControllers.delete(viewer);
  }
}

// Pure: compute clamped sidebar width from mouse position
export const clampSidebarWidth = (
  clientX: number,
  layoutRect: { width: number; left: number },
): number =>
  Math.max(0, Math.min(layoutRect.width * 0.5, clientX - layoutRect.left));

// Pure: clamp a drag x-offset to the allowed pane width range,
// honoring the side (which edge the divider is on) and the
// per-instance max fraction.  Mirrors `clampBlameWidth` but
// parameterised on layout/side/maxFrac for the generic pane divider.
export type PaneSide = "left" | "right";
export const clampPaneWidth = (
  clientX: number,
  layoutRect: { width: number; left: number; right: number },
  side: PaneSide,
  maxFrac: number,
): number => {
  const raw =
    side === "left" ? clientX - layoutRect.left : layoutRect.right - clientX;
  return Math.max(0, Math.min(layoutRect.width * maxFrac, raw));
};

// Build the pane-divider drag handlers with encapsulated dragging
// state, mirroring the `makeBlameDragHandlers` pattern.  The caller
// supplies the layout/pane elements plus `onResize` and `onComplete`
// callbacks; this function owns nothing but the boolean drag flag.
//
// Side effect boundary: handlers toggle the `DRAGGING` / `RESIZING`
// classes and the document-body cursor / userSelect so the UI tracks
// the mouse state, but every pane-width decision flows through
// `onResize` / `onComplete` exactly like the blame divider.
export const makePaneDragHandlers = (
  divider: HTMLElement,
  layout: HTMLElement,
  side: PaneSide,
  maxFrac: number,
  onResize: (width: number) => void,
  onComplete: () => void,
): {
  readonly onDown: (e: MouseEvent) => void;
  readonly onMove: (e: MouseEvent) => void;
  readonly onUp: () => void;
} => {
  let dragging = false;

  return {
    onDown: (e: MouseEvent) => {
      e.preventDefault();
      dragging = true;
      divider.classList.add(DRAGGING);
      layout.classList.add(RESIZING);
      setDragCursor(true);
    },
    onMove: (e: MouseEvent) => {
      if (!dragging) return;
      const rect = layout.getBoundingClientRect();
      onResize(clampPaneWidth(e.clientX, rect, side, maxFrac));
    },
    onUp: () => {
      if (!dragging) return;
      dragging = false;
      divider.classList.remove(DRAGGING);
      layout.classList.remove(RESIZING);
      setDragCursor(false);
      onComplete();
    },
  };
};

// --- CodeEditor: CodeMirror-based file viewer ---

// `SetCodePayload` is dox-owned (`domain/ui/editor.dox`). The
// `set_blame` payload is a single-field record (`{ blocks: [BlameBlock] }`);
// paradox rejects single-field `type` records ("Product type must have
// at least 2 fields"), so the wrapper stays inline while `BlameBlock`
// itself comes from dox.
interface SetBlamePayload {
  blocks: readonly BlameBlock[];
}

interface ReadmeHighlightHook extends EditorHookLike {
  highlight(): void;
}

interface LspReadyPayload {
  session_id: string;
  language: string;
  uri: string;
}

interface CodeEditorHook extends EditorHookLike {
  _saveHandler: ((e: KeyboardEvent) => void) | null;
  _saveButtonHandler: (() => void) | null;
  _commentGutterHandler: ((e: Event) => void) | null;
  _lspState: LspState;
  _lspGen: number;
  _lspController: LspHookController | null;
}

// One gutter click listener per DOM node, independent of hook-instance
// lifetime. A WeakMap so a detached element is collectable.
const gutterListeners = new WeakMap<HTMLElement, (e: Event) => void>();

// The editor's `:w` dispatches this on its own DOM node; the hook owns
// the buffer, so the hook does the push. Ctrl-S calls the same `save`
// directly — one save path, reached two ways.
const saveFileEvent = illuminateDomEvent(validDomEvent("phxSaveFile"));

// Fired on the editor node once a new document is on screen, so the
// comments panel can re-anchor its threads to the new lines.
const editorDocumentEvent = illuminateDomEvent(
  validDomEvent("phxEditorDocument"),
);

// `:w` has to save, and out of the box it does not.
//
// `@replit/codemirror-vim`'s `write` ex command is
//
//     write: function(cm) {
//       if (CM.commands.save) { CM.commands.save(cm); }
//       else if (cm.save)     { cm.save(); }
//     }
//
// with no `else`. This app defines neither, so `:w` was a SILENT no-op —
// no save, no error, and a vim user who typed it believed the file had
// been written. Ctrl-S worked, which is what made the gap easy to miss.
//
// Defined here rather than beside the `vim()` extension in
// `codemirror.ts` so the event name has ONE definition: `saveFileEvent`
// above is it, and `codemirror.ts` cannot import from this module.
//
// Module scope, so it binds once per page instead of per hook mount —
// the handler takes its target from the `cm` it is handed, never from a
// captured hook. `bubbles`, because it goes out on the editor's own node
// and the listener sits on the hook element above it.
Vim.defineEx("write", "w", (cm) => {
  cm.cm6.dom.dispatchEvent(new CustomEvent(saveFileEvent, { bubbles: true }));
});

// Editability is SERVER state: `repo_show_live.ex` renders
// `data-editable="true"` only while a ready workspace of the branch being
// viewed is attached and the editor holds that workspace's copy of the
// file. It is a `data-*` for the reason `data-comments-active` is — the
// only attributes LiveView re-merges onto this ignored node — and the
// save paths read it directly, so there is one representation of it.
const isEditable = (el: HTMLElement): boolean =>
  el.dataset["editable"] === "true";

// A workspace change being played over the editor: its timeline (null
// when it hands off without one) and overlay, and the generation that
// started it, which its hand-off must still be.
interface WorkspaceTransition {
  readonly generation: number;
  readonly timeline: gsap.core.Timeline | null;
  readonly overlay: HTMLElement;
}

// One running change per editor node, keyed by the NODE for the reason
// `gutterListeners` is: it outlives hook instances.
const workspaceTransitions = new WeakMap<HTMLElement, WorkspaceTransition>();
let workspaceGeneration = 0;

// While a change plays, the document under the overlay is the text the
// terminal just replaced: typing into it would be discarded at the
// hand-off, and saving it would write it back over the terminal's
// change. So the editor is read-only and saves are off until then.
const canEdit = (el: HTMLElement): boolean =>
  isEditable(el) && !workspaceTransitions.has(el);

// Re-applied after every `setCode`, not only on mount and patch: a
// language change makes `updateOrInitViewer` build a fresh view, and a
// fresh view starts with its editable compartment at `false`.
const applyEditable = (el: HTMLElement): void => {
  if (canEdit(el)) CM.enableEditing(el);
  else CM.disableEditing(el);
};

// Stop the change playing over `el`, if any: its timeline is killed (so
// its onComplete never runs), its overlay removed and editing restored.
// Called before a newer change, before `setCode` replaces the document
// (another file, or this one reloaded) and when the hook is destroyed.
const cancelWorkspaceChange = (el: HTMLElement): void => {
  const running = workspaceTransitions.get(el);
  if (!running) return;
  workspaceTransitions.delete(el);
  running.timeline?.kill();
  running.overlay.remove();
  applyEditable(el);
};

// What a save sends: the document AND the file it is a copy of, which
// the server renders as `data-path` on this node. The server writes only
// when that path is still the one open, so a save racing a navigation
// cannot land one file's buffer in another.
export const savePayload = (
  el: HTMLElement,
): { content: string; path: string } => ({
  content: CM.getDocumentContent(el),
  path: el.dataset["path"] ?? "",
});

const WORKSPACE_CHANGE_OVERLAY = "workspace-change-overlay";

// Play the change from what the editor shows now to `newText`, then hand
// the text to CodeMirror. A superseded or cancelled change's timeline is
// killed; the generation guard also covers a callback already on its
// way, which then touches nothing (the newer change owns the overlay
// slot). The text is written only while `data-path` still names the file
// the change was started for: after a move to another file the overlay
// just goes away.
// Exported for tests: the overlay's lifetime is the contract.
export const showWorkspaceChange = (
  el: HTMLElement,
  newText: string,
  lang: string,
): void => {
  cancelWorkspaceChange(el);
  workspaceGeneration += 1;
  const generation = workspaceGeneration;
  const path = el.dataset["path"];
  const oldText = CM.getDocumentContent(el);

  const overlay = document.createElement("div");
  overlay.className = `${WORKSPACE_CHANGE_OVERLAY} absolute inset-0 overflow-hidden`;
  overlay.style.zIndex = "5";
  overlay.style.pointerEvents = "none";
  overlay.style.background = "rgb(var(--c-bg))";
  if (getComputedStyle(el).position === "static")
    el.style.position = "relative";
  el.appendChild(overlay);

  const current = (): boolean =>
    workspaceTransitions.get(el)?.generation === generation;

  workspaceTransitions.set(el, { generation, timeline: null, overlay });
  applyEditable(el);

  const timeline = playLineTransition(overlay, oldText, newText, () => {
    if (!current()) return;
    workspaceTransitions.delete(el);
    overlay.remove();
    if (el.dataset["path"] === path) {
      if (CM.getView(el)) CM.replaceDocumentMinimal(el, newText);
      else CM.updateOrInitViewer(el, newText, lang);
    }
    applyEditable(el);
  });
  // A transition that handed off at once has already left the map.
  if (current())
    workspaceTransitions.set(el, { generation, timeline, overlay });
};

export const CodeEditor = {
  mounted(this: CodeEditorHook) {
    this._commentGutterHandler = null;
    // The open file changed in the attached workspace (the terminal wrote
    // it). The server has already made the new text authoritative (the
    // terminal wins over an unsaved buffer); the browser animates what its
    // editor shows → new in a transient overlay and, on the timeline's
    // onComplete, hands the text to CodeMirror as one minimal edit, so a
    // cursor on an untouched line stays put and one undo brings the old
    // text back.
    this.handleEvent("workspaceFileChanged", (payload: unknown) => {
      const { new: next, lang } = payload as WorkspaceFileChangedPayload;
      showWorkspaceChange(this.el, next, lang);
    });

    // A document from the server supersedes a change still playing: its
    // hand-off would otherwise land after this, over the new document.
    this.handleEvent("setCode", (payload: unknown) => {
      const { content, lang } = payload as SetCodePayload;
      cancelWorkspaceChange(this.el);
      CM.updateOrInitViewer(this.el, content, lang);
      applyEditable(this.el);
      this.el.dispatchEvent(
        new CustomEvent(editorDocumentEvent, { bubbles: true }),
      );
    });

    this._lspState = { kind: "idle" };
    this._lspGen = 0;
    this._lspController = makeLspController(this);

    this.handleEvent("lspReady", (payload: unknown) => {
      const { session_id, language, uri } = payload as LspReadyPayload;
      void this._lspController?.attach(session_id, language, uri);
    });

    this.handleEvent("lspStop", () => {
      this._lspController?.detach();
    });

    const save = (): void => {
      if (!canEdit(this.el)) return;
      this.pushEvent("saveFile", savePayload(this.el));
    };
    // Gated on `isEditable`, not `canEdit`: while a workspace change
    // plays, Ctrl+S is still the editor's (a no-op, through `save`), not
    // the browser's "save page".
    this._saveHandler = (e: KeyboardEvent): void => {
      if (!isEditable(this.el)) return;
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    this.el.addEventListener("keydown", this._saveHandler);
    this._saveButtonHandler = save;
    this.el.addEventListener(saveFileEvent, this._saveButtonHandler);

    // Signal Wallaby (and any observer) that the server acknowledged the save.
    // Sets data-save-success on the hook element; Wallaby's assert_has retries
    // until the attribute appears — no :timer.sleep needed.
    this.handleEvent("fileSaved", () => {
      this.el.dataset["saveSuccess"] = "";
    });

    this.handleEvent("setBlame", (payload: unknown) => {
      const { blocks } = payload as SetBlamePayload;
      CM.setBlameData(this.el, blocks);

      if (blocks.length > 0) {
        this.el.classList.add(BLAME_ACTIVE);
        this.el.dataset["blameActive"] = "";
        const saved = Storage.getItem("blameWidth");
        const w = saved ? parseInt(saved, 10) : 300;
        this.el.style.setProperty("--blame-w", `${w}px`);
        setupBlameDivider(this.el, this);
      } else {
        this.el.classList.remove(BLAME_ACTIVE);
        delete this.el.dataset["blameActive"];
        removeBlameDivider(this.el);
      }
    });
    this.handleEvent("clearBlame", () => {
      CM.clearBlameData(this.el);
      this.el.classList.remove(BLAME_ACTIVE);
      delete this.el.dataset["blameActive"];
      removeBlameDivider(this.el);
    });
    // Which lines carry a thread — the gutter marker set. Comment MODE
    // (`data-comments-active`) is deliberately not touched here: the
    // server renders it on `#codeViewer`, because this node is
    // `phx-update="ignore"` and LiveView strips every `data-*` the
    // server does not render from an ignored node on each patch.
    this.handleEvent("setComments", (payload: unknown) => {
      const { lines } = payload as SetCommentsPayload;
      CM.setCommentLineNumbers(this.el, lines);
    });

    // Comment mode: clicking a line number asks the server to open a
    // draft on that line. The form itself is server-rendered inside
    // the comments panel (`OperationWeb.RepoShowLive`) — this hook
    // only reports WHICH line was clicked, so there is exactly one
    // comment surface on the page instead of an overlay competing
    // with the panel for the code column.
    //
    // Held on the hook and removed in `destroyed`, like `_saveHandler`.
    // An anonymous listener leaks: LiveView builds
    // a fresh hook instance whenever a patch detaches and re-attaches
    // the element, and `addEventListener` on the same node then stacks
    // a second copy — every gutter click would push `startComment`
    // twice, cancelling itself out against the server's toggle.
    const editorEl = this.el;
    const pushEv = this.pushEvent.bind(this);
    this._commentGutterHandler = (e: Event): void => {
      // Server-rendered gate (`data-comments-active={@comments_enabled}`),
      // so it is re-asserted by every patch instead of being erased by one.
      if (editorEl.dataset["commentsActive"] === undefined) return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      // CodeMirror line numbers are in .cm-lineNumbers gutter
      const lineEl = target.closest(".cm-lineNumbers .cm-gutterElement");
      if (!lineEl) return;

      const lineText = lineEl.textContent.trim();
      if (!lineText || lineText === "\u200b") return;
      const lineNum = parseInt(lineText, 10);
      if (isNaN(lineNum) || lineNum < 1) return;

      pushEv("startComment", { line: String(lineNum) });
    };
    // Keyed by the NODE, not by the hook instance.
    //
    // `#codeViewer` carries `phx-update="ignore"`, so LiveView keeps the DOM
    // node across a patch and builds a fresh hook instance against it without
    // destroying the old one. `destroyed` never runs, the new instance's
    // `_commentGutterHandler` starts null so it has nothing to remove, and the
    // `addEventListener` below stacks a second copy on the same node. One user
    // click then pushes `startComment` twice and the server's same-line toggle
    // cancels it — open line 3, close line 3 — so the draft never appears.
    //
    // Holding the previous handler against the element is what makes a remount
    // idempotent whether or not `destroyed` ran.
    const previous = gutterListeners.get(editorEl);
    if (previous) editorEl.removeEventListener("click", previous);
    editorEl.addEventListener("click", this._commentGutterHandler);
    gutterListeners.set(editorEl, this._commentGutterHandler);

    applyEditable(this.el);
  },
  // LiveView calls this after merging the server's `data-*` onto the
  // ignored node — which is how a workspace attaching, turning ready or
  // detaching reaches the editor.
  updated(this: CodeEditorHook) {
    applyEditable(this.el);
  },
  destroyed(this: CodeEditorHook) {
    if (this._saveHandler) {
      this.el.removeEventListener("keydown", this._saveHandler);
      this._saveHandler = null;
    }
    if (this._saveButtonHandler) {
      this.el.removeEventListener(saveFileEvent, this._saveButtonHandler);
      this._saveButtonHandler = null;
    }
    if (this._commentGutterHandler) {
      this.el.removeEventListener("click", this._commentGutterHandler);
      // Only drop the node's record if it is still ours: a newer hook
      // instance may already have taken the node over, and clearing its
      // entry would let the NEXT remount stack a duplicate again.
      if (gutterListeners.get(this.el) === this._commentGutterHandler) {
        gutterListeners.delete(this.el);
      }
      this._commentGutterHandler = null;
    }
    this._lspController?.detach();
    this._lspController = null;
    cancelWorkspaceChange(this.el);
    CM.destroyViewer(this.el);
  },
};

// ---------------------------------------------------------------------------
// CommentAnchor
// ---------------------------------------------------------------------------
//
// Pins each thread in the comments panel to the line it is about: a
// thread's top sits level with its line number in the editor, and moves
// with the editor's scroll. Threads that would overlap are pushed down
// below the one above them, so a long thread never hides the next.

// The vertical gap kept between two stacked threads, in px.
const ANCHOR_GAP = 4;

// Where each item goes, given where it wants to be and how tall it is.
// Items are in line order; each sits at its desired top unless the item
// above reaches past that, in which case it sits just below that item.
export const stackAnchors = (
  desired: readonly number[],
  heights: readonly number[],
  gap: number,
): readonly number[] =>
  desired.reduce<{ readonly tops: readonly number[]; readonly floor: number }>(
    ({ tops, floor }, want, i) => {
      const top = Math.max(want, floor);
      return { tops: [...tops, top], floor: top + (heights[i] ?? 0) + gap };
    },
    { tops: [], floor: Number.NEGATIVE_INFINITY },
  ).tops;

interface CommentAnchorHook extends LiveViewHook<never> {
  _scroller: HTMLElement | null;
  _layout: () => void;
  _onWheel: (e: WheelEvent) => void;
  _onClick: (e: MouseEvent) => void;
  _onKey: (e: KeyboardEvent) => void;
  _resize: ResizeObserver;
}

const anchorItems = (el: HTMLElement): readonly HTMLElement[] =>
  Array.from(el.querySelectorAll<HTMLElement>("[data-anchor-line]"));

// The 1-based line an item is about, clamped into the document.
const anchorLine = (item: HTMLElement, lines: number): number =>
  Math.min(
    Math.max(parseInt(item.dataset["anchorLine"] ?? "", 10) || 1, 1),
    lines,
  );

// Back to the panel's plain flow layout — the editor is not showing
// lines (rendered/rich mode, history, image, too-large file).
const unanchor = (el: HTMLElement): void => {
  delete el.dataset["anchored"];
  anchorItems(el).forEach((item) => {
    item.style.removeProperty("top");
  });
};

const layoutAnchors = (el: HTMLElement, view: EditorView): void => {
  const list = el.querySelector<HTMLElement>("[data-anchor-list]");
  if (!list) return;
  el.dataset["anchored"] = "";
  const doc = view.state.doc;
  // In line order, so stacking pushes a later line below an earlier one;
  // the draft form is rendered after the threads whatever its line. The
  // sort is stable, so a draft stays below a thread on the same line.
  const items = [...anchorItems(el)].sort(
    (a, b) => anchorLine(a, doc.lines) - anchorLine(b, doc.lines),
  );
  const origin = list.getBoundingClientRect().top;
  // Line top in viewport coordinates, relative to the list's own top.
  const desired = items.map(
    (item) =>
      view.documentTop +
      view.lineBlockAt(doc.line(anchorLine(item, doc.lines)).from).top -
      origin,
  );
  const tops = stackAnchors(
    desired,
    items.map((item) => item.offsetHeight),
    ANCHOR_GAP,
  );
  items.forEach((item, i) => {
    item.style.top = `${tops[i] ?? 0}px`;
  });
};

// The line a click or key on `target` asks the editor to show: the
// thread it falls in, unless it falls in a control of its own.
const revealTarget = (target: EventTarget | null): number | null => {
  if (!(target instanceof Element)) return null;
  if (target.closest("a, button, input, textarea, form")) return null;
  const thread = target.closest<HTMLElement>("[data-reveal-line]");
  const line = parseInt(thread?.dataset["revealLine"] ?? "", 10);
  return line >= 1 ? line : null;
};

export const CommentAnchor = {
  mounted(this: CommentAnchorHook) {
    this._scroller = null;
    this._layout = (): void => {
      const host = document.getElementById(CODE_VIEWER_ID);
      const view = host ? CM.getView(host) : undefined;
      // The editor rebuilds its view on a language change, so the
      // scroller is re-resolved on every layout, never cached for good.
      const scroller = view ? view.scrollDOM : null;
      if (scroller !== this._scroller) {
        this._scroller?.removeEventListener("scroll", this._layout);
        if (this._scroller) this._resize.unobserve(this._scroller);
        scroller?.addEventListener("scroll", this._layout, { passive: true });
        if (scroller) this._resize.observe(scroller);
        this._scroller = scroller;
      }
      if (view) layoutAnchors(this.el, view);
      else unanchor(this.el);
    };
    // Anchored threads are clipped, not scrolled: the wheel over the
    // panel scrolls the code, and the threads follow it.
    this._onWheel = (e: WheelEvent): void => {
      if (!this._scroller || this.el.dataset["anchored"] === undefined) return;
      e.preventDefault();
      this._scroller.scrollTop += e.deltaY;
    };
    // The editor's height (pane drag, window resize, workspace panel)
    // and the threads' heights both move where everything belongs.
    this._resize = new ResizeObserver(this._layout);
    this._resize.observe(this.el);
    // Clicking a thread shows its line: the editor scrolls to it and
    // highlights it, as a click on the line number does. A drag that
    // selects comment text is a copy, not a request to move the code.
    const reveal = (line: number): void => {
      const host = document.getElementById(CODE_VIEWER_ID);
      if (host) CM.revealLine(host, line);
    };
    this._onClick = (e: MouseEvent): void => {
      const line = revealTarget(e.target);
      if (line === null) return;
      if (window.getSelection()?.isCollapsed === false) return;
      reveal(line);
    };
    this._onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const line = revealTarget(e.target);
      if (line === null) return;
      e.preventDefault();
      reveal(line);
    };
    this.el.addEventListener("wheel", this._onWheel, { passive: false });
    this.el.addEventListener("click", this._onClick);
    this.el.addEventListener("keydown", this._onKey);
    document.addEventListener(editorDocumentEvent, this._layout);
    this._layout();
  },
  // Every patch drops the `top` this hook wrote (LiveView removes
  // attributes the server did not render), and may add or remove threads.
  updated(this: CommentAnchorHook) {
    this._layout();
  },
  destroyed(this: CommentAnchorHook) {
    this._scroller?.removeEventListener("scroll", this._layout);
    this._resize.disconnect();
    this.el.removeEventListener("wheel", this._onWheel);
    this.el.removeEventListener("click", this._onClick);
    this.el.removeEventListener("keydown", this._onKey);
    document.removeEventListener(editorDocumentEvent, this._layout);
    this._scroller = null;
  },
};

// Phoenix Socket is a WebSocket connection; Channels multiplex over it.
// One socket shared across all CodeEditor hooks via lsp-hook-connection's
// refcount — avoids a per-mount WebSocket storm.
//
// The token comes from the <meta name="user-socket-token"> tag rendered by
// root.html.heex; UserSocket.connect/3 rejects every connection without one,
// so an anonymous render returning no token results in a socket that will
// fail to join any channel — that is the desired behavior.
//
// Socket.connect() mutates in place, so the factory returns the socket
// AFTER calling connect() on it.
const makeLspSocket = (): Socket => {
  const token = readUserSocketToken();
  const params = token ? { token } : {};
  const s = new Socket("/ci/socket", { params });
  s.connect();
  return s;
};

// ---------------------------------------------------------------------------
// LspHookController
// ---------------------------------------------------------------------------
//
// Owns the LSP attach / detach lifecycle for a single CodeEditor hook. The
// shared Phoenix socket + the `_lspGen` race fence live in
// `lsp-hook-connection.ts`; this controller is a thin object wrapper so
// tests can drive `attach` / `detach` directly without poking module-level
// helpers, and so the dependencies (socket factory, `connectLSP`, and the
// CodeMirror `enableLsp` adapter) are constructor-injected rather than
// hard-wired to the production singletons.

export interface LspHookDeps {
  readonly socketFactory: SocketFactory;
  readonly connect: AttachDeps["connect"];
  readonly enableLsp: (
    el: HTMLElement,
    client: Parameters<AttachDeps["enableLsp"]>[0],
    uri: string,
  ) => () => void;
}

export class LspHookController {
  constructor(
    private readonly hook: CodeEditorHook,
    private readonly deps: LspHookDeps,
  ) {}

  attach(sessionId: string, language: string, uri: string): Promise<void> {
    return attachLspConnection({
      owner: this.hook,
      sessionId,
      language,
      uri,
      deps: {
        socketFactory: this.deps.socketFactory,
        connect: this.deps.connect,
        enableLsp: (client, u) => this.deps.enableLsp(this.hook.el, client, u),
      },
    });
  }

  detach(): void {
    detachLspConnection(this.hook);
  }
}

// Build the controller for a hook with the production dependencies — split
// out so tests can stub `socketFactory` / `connect` / `enableLsp` independently.
const makeLspController = (hook: CodeEditorHook): LspHookController =>
  new LspHookController(hook, {
    socketFactory: makeLspSocket,
    connect: connectLSP,
    enableLsp: (el, client, u) => CM.enableLsp(el, client, u),
  });

// --- PaneDivider: drag-resize between sidebar and editor ---

interface PaneDividerHook extends EditorHookLike {
  _cleanup: (() => void) | null;
}

/** Generic pane divider — resizes an adjacent pane by drag.
 *
 *  Config via data attributes on the hook element:
 *    data-layout   CSS selector for the flex container (default: ".code-editor-layout")
 *    data-pane     CSS selector for the target pane    (default: ".code-sidebar")
 *    data-side     "left" or "right"                   (default: "left")
 *    data-persist  LocalStorageKey tag for width        (default: "sidebarW")
 *    data-default  default width on double-click reset  (default: `codeSidebarDefaultWidth`)
 *    data-max      max fraction of layout width (0-1)   (default: `codeSidebarMaxFraction`)
 *
 *  The dragged width is written to localStorage under `data-persist` AND
 *  published as the CSS custom property `--pane-<data-persist>` on <html>.
 *  A pane whose width must survive a LiveView patch reads that property as
 *  its flex-basis instead of carrying an inline `style` — `workspace-panel`
 *  in assets/css/app.css is the one that does.
 *
 *  The `data-default` / `data-max` fallbacks come from the dox constants
 *  `codeSidebarDefaultWidth` / `codeSidebarMaxFraction` so HEEx templates
 *  that omit those attributes (e.g. the blob-view sidebar divider in
 *  `repo_show_live.ex`) and HEEx templates that set them explicitly
 *  (e.g. `diff_component.ex`) resolve to the same number — one source
 *  of truth across server-rendered HTML and client-side hook code.
 */
export const PaneDivider = {
  mounted(this: PaneDividerHook) {
    const divider = this.el;
    const layoutSel = divider.dataset["layout"] ?? ".code-editor-layout";
    const paneSel = divider.dataset["pane"] ?? ".code-sidebar";
    const side = (divider.dataset["side"] ?? "left") as "left" | "right";
    // data-persist carries a `LocalStorageKey` tag (e.g. "sidebarW"); a typo
    // in the heex throws here instead of silently picking a phantom slot.
    const persistKey: LocalStorageKey = Storage.validLocalStorageKey(
      divider.dataset["persist"] ?? "sidebarW",
    );
    const defaultW = parseInt(
      divider.dataset["default"] ?? String(codeSidebarDefaultWidth),
      10,
    );
    const maxFrac = parseFloat(
      divider.dataset["max"] ?? String(codeSidebarMaxFraction),
    );

    const layout = divider.closest<HTMLElement>(layoutSel);
    const pane = layout?.querySelector<HTMLElement>(paneSel) ?? null;
    if (!layout || !pane) {
      this._cleanup = null;
      return;
    }

    // Width has to survive a LiveView patch, and an inline `style` on the
    // pane does not: morphdom syncs that element's attributes from the
    // server's render and either resets the drag's value (when the server
    // renders a `style`) or removes it outright (when it does not).
    //
    // `.code-sidebar` already dodges this by reading `var(--sidebar-w)`,
    // which `persistSidebarWidth` writes onto <html> — outside the patch
    // root, where morphdom never looks. Generalise that rather than leave
    // it special-cased: publish EVERY pane's width as `--pane-<key>`, so a
    // pane opts in by using it as its flex-basis. Panes that keep the
    // inline style are unaffected; the extra property is inert for them.
    const paneVar = `--pane-${persistKey}`;

    const publish = (w: number): void => {
      document.documentElement.style.setProperty(paneVar, `${w}px`);
    };

    // A property on <html> restyles the whole document, and a drag fires
    // mousemove far more often than the screen paints — on the diff view
    // that is a full-page style recalc per event. Publish the newest width
    // once per frame instead; the pane's own flex still follows the mouse.
    let frame: number | null = null;
    let latest = 0;
    const publishOnFrame = (w: number): void => {
      latest = w;
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        publish(latest);
      });
    };
    const cancelFrame = (): void => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };

    // Synchronous, and it wins: a frame still queued from the drag would
    // otherwise land after it with an unrounded width.
    const persist = (w: number): void => {
      cancelFrame();
      Storage.setItem(persistKey, String(w));
      publish(w);
    };

    // Restore persisted width — skip if the pane has its own hook managing
    // initial width (e.g. JobDetailPanel handles animated open).
    if (!pane.hasAttribute("phx-hook")) {
      const saved = Storage.getItem(persistKey);
      if (saved) {
        const w = parseInt(saved, 10);
        if (w > 0) {
          pane.style.flex = `0 0 ${w}px`;
          publish(w);
        }
      }
    }

    const { onDown, onMove, onUp } = makePaneDragHandlers(
      divider,
      layout,
      side,
      maxFrac,
      (w) => {
        pane.style.flex = `0 0 ${w}px`;
        publishOnFrame(w);
        clearGsapResiduals(pane);
      },
      () => {
        const finalW = Math.round(pane.getBoundingClientRect().width);
        persist(finalW);
        // Legacy compat: code-browser also writes to cookie + CSS var
        if (persistKey === "sidebarW") {
          persistSidebarWidth(finalW);
        }
      },
    );

    const onDblClick = (): void => {
      pane.style.flex = `0 0 ${defaultW}px`;
      persist(defaultW);
      if (persistKey === "sidebarW") {
        layout.classList.remove(SIDEBAR_HIDDEN);
        persistSidebarWidth(defaultW);
      }
    };

    divider.addEventListener("mousedown", onDown);
    divider.addEventListener("dblclick", onDblClick);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    this._cleanup = (): void => {
      cancelFrame();
      divider.removeEventListener("mousedown", onDown);
      divider.removeEventListener("dblclick", onDblClick);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  },
  destroyed(this: PaneDividerHook) {
    if (this._cleanup) this._cleanup();
  },
};

// --- ReadmeHighlight: syntax highlight fenced code blocks in rendered markdown ---

export const ReadmeHighlight = {
  highlight(this: EditorHookLike) {
    this.el.querySelectorAll("pre code").forEach(function (block: Element) {
      const pre = block.parentElement;
      if (pre && pre.tagName === "PRE") {
        pre.style.cssText =
          "background:rgb(var(--c-bg-secondary)) !important;border-radius:2px;padding:1rem;overflow:auto;border:var(--border-w) solid rgb(var(--c-border))";
        (block as HTMLElement).style.cssText =
          "font-family:'DejaVu Mono',monospace;font-size:0.8125rem;line-height:1.6;color:rgb(var(--c-syn-text));background:transparent !important";
      }
      const cls = Array.from(block.classList).find(function (c: string) {
        return c.startsWith("language-");
      });
      let lang = cls ? cls.replace("language-", "") : "";
      if (!lang && block.className) {
        const raw = block.className.trim();
        if (/^[a-z][a-z0-9+#]*$/i.test(raw)) lang = raw;
      }
      if (lang) void CM.highlightElement(block as HTMLElement, lang);
    });
  },
  mounted(this: ReadmeHighlightHook) {
    this.highlight();
  },
  updated(this: ReadmeHighlightHook) {
    this.highlight();
  },
};

// --- VimToggle: toggle vim mode on click ---

export const VimToggle = {
  mounted(this: EditorHookLike) {
    const btn = this.el;
    const update = function () {
      const on = CM.isVimMode();
      btn.innerHTML = on
        ? 'Vim <icon class="nf-icon flex-shrink-0" aria-hidden="true">\uf00c</icon>'
        : "Vim";
      btn.style.color = on ? "rgb(var(--c-accent))" : "";
    };
    update();
    btn.addEventListener("click", function () {
      // Presence, not visibility. RichToggle used to hide the viewer with
      // `display: none` and leave it in the DOM, so this had to test the
      // style; now the server renders either the code viewer or the rich
      // host, never both, and nothing writes `display` at all. Testing it
      // would be testing a condition with no writer.
      const v = document.getElementById(CODE_VIEWER_ID);

      if (v) {
        CM.toggleVimMode(v);
      } else {
        const nowOn = !CM.isVimMode();
        Storage.setItem("editorVimMode", String(nowOn));
      }

      update();
    });
  },
};

// --- EditorModeButton: one transport for every server-driven editor mode ---

// Pure reporter: it pushes the mode its button carries plus whatever buffer is
// live, and touches no DOM. Each mode's host is rendered by the server
// (`repo_show_live.ex`), so this only has to say which mode and hand over the
// text the user can see.
//
// It replaces `RichToggle`, which (a) built its own EasyMDE container and
// spliced it into a region LiveView manages — the patch caused by its own push
// then deleted it, so Rich flashed and went blank — and (b) was the only mode
// button carrying a buffer at all. Rendered was a bare `phx-click`, so
// Rich → type → Rendered dropped the text, and the Rendered → Source hop
// afterwards re-seeded CodeMirror from disk. One hook on both buttons means
// one buffer reader and no mode that silently loses work.
export const EditorModeButton = {
  mounted(this: EditorHookLike) {
    const pushEvent = this.pushEvent.bind(this);
    // Dox-owned (`DataAttr.editorMode` in `ui/ci.dox`), read back through the
    // dataset's camelCase form of the same name.
    const mode = this.el.dataset["editorMode"];

    this.el.addEventListener("click", () => {
      if (!mode) return;
      pushEvent("setEditorMode", { mode, ...liveBuffer() });
    });
  },
};

// The buffer currently on screen, as a `{content}` fragment to splat into the
// push payload — `{}` when no editor is mounted. The single reader of "what
// text does the user have right now", used by every mode button, so two
// transports cannot disagree about it.
//
// Rich mode is read through the textarea rather than the EasyMDE instance
// because `MarkdownEditor` owns that instance and this module cannot reach
// it. That makes `ta.value` an interface, not an implementation detail:
// `MarkdownEditor` mirrors the editor onto it on every change, and
// `hooks-markdown-editor.test.ts` pins that mirror. Break it and the test
// fails here rather than this silently reporting stale text.
const liveBuffer = (): { content?: string } => {
  const rich = document.getElementById(RICH_EDITOR_ID);
  if (rich instanceof HTMLTextAreaElement) return { content: rich.value };

  const viewer = document.getElementById(CODE_VIEWER_ID);
  const cm = viewer ? CM.getView(viewer) : null;
  return cm ? { content: cm.state.doc.toString() } : {};
};

// --- KeyboardShortcuts: global shortcuts for code browser ---

// `codeBrowserKeyAction` and `CodeBrowserAction` now live in
// ./keyboard-actions alongside the omnisearch decoder. Re-export for callers
// that already import from "./code-browser".
export {
  codeBrowserKeyAction,
  type CodeBrowserAction,
} from "./keyboard-actions";
import { codeBrowserKeyAction } from "./keyboard-actions";

// GSAP sidebar close animation config
const SIDEBAR_CLOSE_ANIM = {
  rotationY: -67.5,
  transformPerspective: 4000,
  transformOrigin: "right center",
  opacity: 0,
  duration: 0.5,
  ease: "power2.inOut",
} as const;

interface KeyboardShortcutsHook extends EditorHookLike {
  _handler: (e: KeyboardEvent) => void;
}

export const KeyboardShortcuts = {
  mounted(this: KeyboardShortcutsHook) {
    const el = this.el;
    const pushEvent = this.pushEvent.bind(this);

    // Server can request sidebar open (e.g. entering history mode). The
    // close animation persists `--sidebar-w: 0px` to localStorage / cookie
    // so the closed state survives navigation; without restoring a
    // non-zero width, removing the inline style + class still leaves the
    // CSS rule `flex: 0 0 var(--sidebar-w, 260px)` clamped at 0 and the
    // sidebar stays invisible.
    this.handleEvent("openSidebar", () => {
      const sidebar = el.querySelector<HTMLElement>(".code-sidebar");
      if (sidebar) {
        sidebar.removeAttribute("style");
        el.classList.remove(SIDEBAR_HIDDEN);
        // Restore the cookie / CSS-var to the shared default so the
        // CSS rule `flex: 0 0 var(--sidebar-w, …px)` resolves the same
        // here as on first paint. Sourced from `helpers.dox` so this
        // tracks the HEEx `data-default` on the PaneDivider without
        // a hand-mirrored number.
        persistSidebarWidth(codeSidebarDefaultWidth);
      }
    });

    const animateSidebarClose = (): void => {
      const sidebar = el.querySelector<HTMLElement>(".code-sidebar");
      if (!sidebar) return;
      sidebar.style.overflow = "hidden";
      const startWidth = sidebar.getBoundingClientRect().width;
      sidebar.style.flex = `0 0 ${startWidth}px`;
      const tween = gsap.fromTo(
        sidebar,
        {
          rotationY: 0,
          transformPerspective: 4000,
          transformOrigin: "right center",
          marginLeft: 0,
          opacity: 1,
        },
        {
          ...SIDEBAR_CLOSE_ANIM,
          marginLeft: -startWidth,
          onComplete: () => {
            tween.kill();
            sidebar.removeAttribute("style");
            sidebar.style.flex = "0 0 0px";
            sidebar.style.minWidth = "0";
            sidebar.style.overflow = "hidden";
            persistSidebarWidth(0);
          },
        },
      );
    };

    this.el.addEventListener("click", (e: Event) => {
      const target =
        e.target instanceof Element
          ? e.target.closest(`.${SIDEBAR_CLOSE_BTN}`)
          : null;
      if (!target) return;
      e.preventDefault();
      animateSidebarClose();
    });

    const isSidebarOpen = (): boolean => {
      const sidebar = el.querySelector(".code-sidebar");
      return !!sidebar && sidebar.getBoundingClientRect().width > 20;
    };

    this._handler = (e: KeyboardEvent): void => {
      if (isEditableTarget(e)) return;
      const result = codeBrowserKeyAction(
        e.key,
        e.ctrlKey,
        e.metaKey,
        e.altKey,
        isSidebarOpen(),
      );

      switch (result.action) {
        case "toggle_shortcuts": {
          e.preventDefault();
          const overlay = document.getElementById(SHORTCUTS_OVERLAY_ID);
          if (overlay) overlay.classList.toggle("hidden");
          break;
        }
        case "toggle_sidebar":
          e.preventDefault();
          if (result.sidebarOpen) {
            animateSidebarClose();
          } else {
            pushEvent("toggleSidebar", {});
          }
          break;
        case "toggle_blame":
          e.preventDefault();
          pushEvent("toggleBlame", {});
          break;
        case "focus_search": {
          e.preventDefault();
          const input = document.querySelector<HTMLInputElement>(
            'input[name="query"]',
          );
          if (input) input.focus();
          break;
        }
        case "permalink":
          e.preventDefault();
          pushEvent("permalink", {});
          break;
        case "close_shortcuts": {
          const overlay = document.getElementById(SHORTCUTS_OVERLAY_ID);
          if (overlay && !overlay.classList.contains("hidden")) {
            overlay.classList.add("hidden");
            e.preventDefault();
          }
          break;
        }
      }
    };
    document.addEventListener("keydown", this._handler);
  },

  destroyed(this: KeyboardShortcutsHook) {
    document.removeEventListener("keydown", this._handler);
  },
};
